import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

export default function Categories() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/categories");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "" }); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || "" }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Categoria aggiornata");
      } else {
        await api.post("/categories", form);
        toast.success("Categoria creata");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la categoria "${r.name}"?`)) return;
    try {
      await api.delete(`/categories/${r.id}`);
      toast.success("Categoria eliminata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Categorie"
        subtitle="Organizza i tuoi prodotti per categoria"
        action={
          <button
            data-testid="new-category-btn"
            onClick={openNew}
            className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> Nuova Categoria
          </button>
        }
      />

      <div className="p-8">
        <div className="bg-white border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Nome</th>
                <th className="px-4 py-3 label-eyebrow">Descrizione</th>
                <th className="px-4 py-3 label-eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="3" className="px-4 py-12 text-center text-neutral-500">Nessuna categoria. Aggiungi la prima.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.description || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-category-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-category-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-red-100 text-red-600 transition-colors"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-md max-h-[95vh] flex flex-col overflow-y-auto" data-testid="category-form">
            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuova"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Categoria</h2>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="label-eyebrow block mb-2">Nome *</span>
                <input required data-testid="category-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Descrizione</span>
                <textarea data-testid="category-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="category-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
